import React, { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useSearchParams } from "react-router-dom";
import { useSettings } from "@/contexts/SettingsContext";
import { api } from "@/lib/api";
import RequestFormDialog from "@/components/RequestFormDialog";
import Wordmark from "@/components/Wordmark";
import { Menu, X, Phone, Mail, MapPin, Clock, MessageCircle, User } from "lucide-react";

const links = [
  { to: "/", label: "Ana Sayfa" },
  { to: "/hakkimizda", label: "Hakkımızda" },
  { to: "/hizmetler", label: "Hizmetler" },
  { to: "/iletisim", label: "İletişim" },
];

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid"];

function captureUtm(params) {
  const found = {};
  UTM_KEYS.forEach((k) => { const v = params.get(k); if (v) found[k] = v; });
  if (Object.keys(found).length === 0) return;
  found.referrer = document.referrer || "";
  found.captured_at = new Date().toISOString();
  localStorage.setItem("asg_utm", JSON.stringify(found));
}

function injectTrackingScripts(settings) {
  if (!settings) return;
  const gaId = settings.google_analytics_id;
  const adsId = settings.google_ads_id;

  // Remove previously injected ones to avoid duplicates
  document.querySelectorAll("script[data-asg-tracking]").forEach((s) => s.remove());

  if (!gaId && !adsId) {
    delete window.gtag;
    delete window.dataLayer;
    return;
  }

  const primary = gaId || adsId;
  const s1 = document.createElement("script");
  s1.async = true;
  s1.src = `https://www.googletagmanager.com/gtag/js?id=${primary}`;
  s1.setAttribute("data-asg-tracking", "1");
  document.head.appendChild(s1);

  const s2 = document.createElement("script");
  s2.setAttribute("data-asg-tracking", "1");
  s2.text = `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
window.gtag = gtag;
gtag('js', new Date());
${gaId ? `gtag('config', '${gaId}');` : ""}
${adsId ? `gtag('config', '${adsId}');` : ""}`;
  document.head.appendChild(s2);
}

export default function PublicLayout() {
  const { settings } = useSettings();
  const loc = useLocation();
  const [params] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formProduct, setFormProduct] = useState(null);

  useEffect(() => {
    captureUtm(params);
  }, [params]);

  useEffect(() => {
    injectTrackingScripts(settings);
  }, [settings?.google_ads_id, settings?.google_analytics_id]);

  useEffect(() => {
    setOpen(false);
    api.post("/analytics/pageview", { path: loc.pathname }).catch(() => {});
    if (window.gtag) {
      const gaId = settings?.google_analytics_id;
      if (gaId) window.gtag("event", "page_view", { page_path: loc.pathname });
    }
  }, [loc.pathname, settings?.google_analytics_id]);

  const wa = (settings?.whatsapp || "").replace(/[^0-9]/g, "");
  const waLink = wa ? `https://wa.me/${wa}` : "#";

  const openForm = (productKey) => {
    setFormProduct(productKey || null);
    setFormOpen(true);
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--asg-bg)" }}>
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-24 flex items-center justify-between gap-6">
          <Wordmark size="md" testid="header-wordmark" />

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={`text-sm tracking-wide transition-colors ${
                  loc.pathname === l.to
                    ? "text-black font-semibold border-b-2 pb-0.5"
                    : "text-black/70 hover:text-black font-medium"
                }`}
                style={loc.pathname === l.to ? { borderColor: "var(--asg-primary)" } : {}}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              data-testid="header-whatsapp-btn"
              className="hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-full border border-emerald-500/50 text-sm text-emerald-700 hover:bg-emerald-50 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
            <Link
              to="/musteri"
              data-testid="header-customer-portal-btn"
              className="hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[color:var(--asg-border)] text-sm text-[color:var(--asg-text)] hover:bg-[color:var(--asg-surface)] transition-colors"
            >
              <User className="w-4 h-4" style={{ color: "var(--asg-primary-dark)" }} />
              Müşteri Girişi
            </Link>
            <button
              className="lg:hidden text-[color:var(--asg-text)] ml-1"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label="Menü"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/10 bg-[color:var(--asg-surface)]/95 backdrop-blur-xl">
            <div className="max-w-7xl mx-auto px-5 py-4 grid gap-3">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="text-zinc-200 py-2 border-b border-white/5"
                  data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                >
                  {l.label}
                </Link>
              ))}
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className="text-emerald-400 py-2"
                data-testid="mobile-whatsapp"
              >
                WhatsApp ile İletişim
              </a>
              <Link
                to="/musteri"
                className="text-sky-300 py-2"
                data-testid="mobile-customer-portal"
              >
                Müşteri Girişi
              </Link>
            </div>
          </div>
        )}
      </header>

      <main>
        <Outlet context={{ openForm }} />
      </main>

      <footer className="border-t border-[color:var(--asg-border)] mt-24 bg-[color:var(--asg-surface)]">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-4 gap-10">
          <div>
            <img src={settings.logo_url} alt="Aydınlar Sigorta Global" className="h-16 mb-4" />
            <p className="text-sm text-[color:var(--asg-muted)] leading-relaxed max-w-xs">
              {settings.tagline}. Uzman kadromuz ve Türkiye'nin lider sigorta şirketleriyle
              yanınızdayız.
            </p>
          </div>
          <div>
            <h4 className="font-heading text-[color:var(--asg-text)] text-lg mb-4">Kurumsal</h4>
            <ul className="space-y-2 text-sm text-[color:var(--asg-muted)]">
              {links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="hover:text-[color:var(--asg-text)] transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-heading text-[color:var(--asg-text)] text-lg mb-4">İletişim</h4>
            <ul className="space-y-2 text-sm text-[color:var(--asg-muted)]">
              <li className="flex items-center gap-2"><Phone className="w-4 h-4 asg-primary" /> {settings.phone}</li>
              <li className="flex items-center gap-2"><Mail className="w-4 h-4 asg-primary" /> {settings.email}</li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 asg-primary mt-0.5" /> {settings.address}</li>
              <li className="flex items-center gap-2"><Clock className="w-4 h-4 asg-primary" /> {settings.working_hours}</li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading text-[color:var(--asg-text)] text-lg mb-4">Hemen İletişime Geçin</h4>
            <div className="flex flex-col gap-3">
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full border border-emerald-500/50 text-emerald-700 hover:bg-emerald-50 text-sm transition"
                data-testid="footer-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp ile Yaz
              </a>
              <button
                onClick={() => openForm(null)}
                className="asg-btn-primary inline-flex items-center justify-center h-11 px-5 rounded-full text-sm font-semibold"
                data-testid="footer-request-form-btn"
              >
                Teklif Al
              </button>
            </div>
          </div>
        </div>
        <div className="border-t border-[color:var(--asg-border)] py-6 text-center text-xs text-[color:var(--asg-muted)]">
          © {new Date().getFullYear()} {settings.site_name}. Tüm hakları saklıdır.
        </div>
      </footer>

      <a
        href={waLink}
        target="_blank"
        rel="noreferrer"
        data-testid="floating-whatsapp"
        className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-emerald-500 hover:bg-emerald-400 flex items-center justify-center shadow-2xl shadow-emerald-900/40 transition-transform hover:-translate-y-0.5"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-6 h-6 text-white" />
      </a>

      <RequestFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        initialProductKey={formProduct}
      />
    </div>
  );
}
